import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Card, CardBody, CardHeader, Button, Spinner } from "@heroui/react";
import {
    LuArrowLeft,
    LuArrowRight,
    LuSave,
    LuUser,
    LuMapPin,
    LuPhone,
    LuImage,
} from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";
import { usePostData, useFetchData } from "@/hooks";
import { showToast, clearToast } from "@/utils";
import { ThemedText, PageHeader, Stepper, ErrorState } from "@/components/ui";
import {
    GeneralInfoStep,
    AddressStep,
    ContactStep,
    MediaStep,
} from "./components";

const STEPS = [
    { id: 1, title: "Informasi Umum", icon: LuUser },
    { id: 2, title: "Alamat", icon: LuMapPin },
    { id: 3, title: "Kontak", icon: LuPhone },
    { id: 4, title: "Media", icon: LuImage },
];

const CustomerCreate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    const [currentStep, setCurrentStep] = useState(1);

    // Image state as object
    const [imageState, setImageState] = useState({
        items: [],
        deleted: [],
    });

    // Contact numbers state (start with one empty for create, empty for edit - will load from API)
    const [contacts, setContacts] = useState(() =>
        isEdit ? [] : [{ contact_type_id: "", number: "" }],
    );

    // React Hook Form
    const {
        register,
        handleSubmit,
        watch,
        trigger,
        reset,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            // Main table (customers) - FLAT
            name: "",
            description: "",

            // Related table (addresses) - NESTED OBJECT
            address: {
                full_address: "",
                neighborhood_unit: "",
                sub_neighborhood_unit: "",
                village: "",
                district: "",
                city: "",
                province: "",
                postal_code: "",
                lat: null,
                lng: null,
                google_map_url: "",
            },
        },
    });

    // Fetch initial data for edit mode
    const fetchUrl = isEdit ? `customers/${id}/edit` : null;
    const {
        data: initialData,
        isLoading: isFetching,
        error: fetchError,
        refetch,
    } = useFetchData(fetchUrl);

    // Populate form when editing
    useEffect(() => {
        if (isEdit && initialData) {
            reset({
                name: initialData.name || "",
                description: initialData.description || "",
                address: {
                    full_address:
                        initialData.addresses?.[0]?.full_address || "",
                    neighborhood_unit:
                        initialData.addresses?.[0]?.neighborhood_unit || "",
                    sub_neighborhood_unit:
                        initialData.addresses?.[0]?.sub_neighborhood_unit || "",
                    village: initialData.addresses?.[0]?.village || "",
                    district: initialData.addresses?.[0]?.district || "",
                    city: initialData.addresses?.[0]?.city || "",
                    province: initialData.addresses?.[0]?.province || "",
                    postal_code: initialData.addresses?.[0]?.postal_code || "",
                    lat: initialData.addresses?.[0]?.lat || null,
                    lng: initialData.addresses?.[0]?.lng || null,
                    google_map_url:
                        initialData.addresses?.[0]?.google_map_url || "",
                },
            });

            // Set existing images
            if (initialData.images && initialData.images.length > 0) {
                const existingImages = initialData.images.map((img) => ({
                    id: img.id,
                    preview: img.url_600,
                    isExisting: true,
                }));
                setImageState((prev) => ({ ...prev, items: existingImages }));
            }

            // Set existing contacts
            if (
                initialData.contact_numbers &&
                initialData.contact_numbers.length > 0
            ) {
                const existingContacts = initialData.contact_numbers.map(
                    (cn) => ({
                        id: cn.id,
                        contact_type_id: cn.contact_type_id,
                        number: cn.number,
                        isExisting: true,
                    }),
                );
                setContacts(existingContacts);
            }
        }
    }, [isEdit, initialData, reset]);

    // Mutation
    const mutationEndpoint = isEdit
        ? `customers/${id}/update`
        : "customers/store";
    const customerMutation = usePostData(mutationEndpoint);

    // Validation per step
    const validateStep = async (step) => {
        switch (step) {
            case 1:
                return await trigger(["name"]);
            case 2:
                return true;
            case 3:
                return true;
            case 4:
                return true;
            default:
                return true;
        }
    };

    const handleNext = async () => {
        const isValid = await validateStep(currentStep);
        if (isValid) {
            clearToast();
            setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
        } else {
            showToast(
                "error",
                "Validasi Gagal",
                "Mohon lengkapi data yang wajib diisi.",
            );
        }
    };

    const handlePrev = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 1));
    };

    const onSubmit = (data) => {
        // Filter valid contacts (ones with both type and number)
        const validContacts = contacts.filter(
            (c) => c.contact_type_id && c.number,
        );

        // Build payload
        const payload = {
            name: data.name,
            active_status_id: 1,
            ...(data.description && { description: data.description }),
            // Address data
            ...(data.address?.full_address && {
                address: {
                    full_address: data.address.full_address,
                    neighborhood_unit: data.address.neighborhood_unit || null,
                    sub_neighborhood_unit:
                        data.address.sub_neighborhood_unit || null,
                    village: data.address.village || null,
                    district: data.address.district || null,
                    city: data.address.city || null,
                    province: data.address.province || null,
                    postal_code: data.address.postal_code || null,
                    lat: data.address.lat || null,
                    lng: data.address.lng || null,
                    google_map_url: data.address.google_map_url || null,
                },
            }),
            // Contact numbers data
            ...(validContacts.length > 0 && {
                contact_numbers: validContacts.map((c) => ({
                    contact_type_id: c.contact_type_id,
                    number: c.number,
                })),
            }),
        };

        // Handle FormData for images
        let submitData = payload;
        let headers = {};
        let method = isEdit ? "put" : "post";

        const newImages = imageState.items.filter(
            (img) => img.file && !img.isExisting,
        );
        const hasNewImages = newImages.length > 0;
        const hasDeletedImages = imageState.deleted.length > 0;

        if (hasNewImages || hasDeletedImages) {
            const formData = new FormData();

            // Flatten payload into FormData
            formData.append("name", payload.name);
            formData.append("active_status_id", payload.active_status_id);
            if (payload.description) {
                formData.append("description", payload.description);
            }

            // Address fields
            if (payload.address) {
                Object.entries(payload.address).forEach(([key, value]) => {
                    if (value !== null && value !== "") {
                        formData.append(`address[${key}]`, value);
                    }
                });
            }

            // Contact numbers
            if (payload.contact_numbers) {
                payload.contact_numbers.forEach((cn, index) => {
                    formData.append(
                        `contact_numbers[${index}][contact_type_id]`,
                        cn.contact_type_id,
                    );
                    formData.append(
                        `contact_numbers[${index}][number]`,
                        cn.number,
                    );
                });
            }

            // New images (file_type defined in backend)
            newImages.forEach((img, index) => {
                formData.append(`images[${index}]`, img.file);
            });

            // Deleted images
            imageState.deleted.forEach((imgId, index) => {
                formData.append(`deleted_images[${index}]`, imgId);
            });

            if (isEdit) {
                formData.append("_method", "PUT");
                method = "post";
            }

            submitData = formData;
            headers = { "Content-Type": "multipart/form-data" };
        }

        customerMutation.mutate(submitData, {
            method,
            headers,
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    `Pelanggan berhasil ${isEdit ? "diperbarui" : "ditambahkan"}.`,
                );
                navigate("/customers");
            },
            onError: (err) => {
                let message =
                    "Terjadi kesalahan pada sistem. Silakan coba beberapa saat lagi.";
                if (err.response?.status === 422) {
                    message =
                        "Mohon periksa kembali input Anda. Pastikan semua kolom terisi dengan benar.";
                }
                showToast("error", "Gagal", message);
            },
        });
    };

    // Error state for edit mode
    if (isEdit && fetchError) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data pelanggan. Silakan coba lagi."
                onAction={refetch}
            />
        );
    }

    return (
        <div className="mx-auto flex max-w-3xl flex-col gap-6">
            {/* Header */}
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title={isEdit ? "Edit Pelanggan" : "Tambah Pelanggan"}
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Pelanggan", href: "/customers" },
                        { label: isEdit ? "Edit" : "Tambah" },
                    ]}
                />
            </div>

            {/* Stepper */}
            <Card className="border-default-200 border" shadow="none">
                <CardBody className="p-6">
                    <Stepper steps={STEPS} currentStep={currentStep} />
                </CardBody>
            </Card>

            {/* Step Content */}
            <form onSubmit={(e) => e.preventDefault()} noValidate>
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="bg-default-100/70 px-6 py-4">
                        <ThemedText className="font-semibold">
                            {STEPS[currentStep - 1].title}
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-6">
                        {/* Loading state for edit mode */}
                        {isEdit && isFetching ? (
                            <div className="flex h-48 w-full items-center justify-center">
                                <Spinner
                                    label="Memuat data..."
                                    color="primary"
                                />
                            </div>
                        ) : (
                            <>
                                {/* Step 1: General Info */}
                                {currentStep === 1 && (
                                    <GeneralInfoStep
                                        register={register}
                                        errors={errors}
                                    />
                                )}

                                {/* Step 2: Address */}
                                {currentStep === 2 && (
                                    <AddressStep
                                        register={register}
                                        errors={errors}
                                        watch={watch}
                                        setValue={setValue}
                                    />
                                )}

                                {/* Step 3: Contact */}
                                {currentStep === 3 && (
                                    <ContactStep
                                        contacts={contacts}
                                        setContacts={setContacts}
                                    />
                                )}

                                {/* Step 4: Media */}
                                {currentStep === 4 && (
                                    <MediaStep
                                        imageState={imageState}
                                        setImageState={setImageState}
                                    />
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>

                {/* Navigation Buttons */}
                <div className="mt-6 flex justify-between">
                    <Button
                        type="button"
                        variant="flat"
                        onPress={
                            currentStep === 1
                                ? () => navigate("/customers")
                                : handlePrev
                        }
                        size="sm"
                        startContent={<LuArrowLeft size={18} />}
                    >
                        {currentStep === 1 ? "Batal" : "Sebelumnya"}
                    </Button>
                    <div className="flex gap-2">
                        {currentStep < STEPS.length ? (
                            <Button
                                type="button"
                                color="primary"
                                onPress={handleNext}
                                endContent={<LuArrowRight size={18} />}
                                size="sm"
                            >
                                Selanjutnya
                            </Button>
                        ) : (
                            <Button
                                color="primary"
                                type="button"
                                isLoading={customerMutation.isLoading}
                                onPress={handleSubmit(onSubmit)}
                                size="sm"
                                startContent={
                                    !customerMutation.isLoading && (
                                        <LuSave size={18} />
                                    )
                                }
                            >
                                {isEdit
                                    ? "Simpan Perubahan"
                                    : "Simpan Pelanggan"}
                            </Button>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CustomerCreate;
